import React, { useEffect, useRef, useState } from 'react';
import './WaveformVisualizer.scss';

interface WaveformVisualizerProps {
    audioUrl: string;
    isPlaying: boolean;
    onPlayPause: () => void;
    onSeek: (progress: number) => void;
    duration: number;
    formattedDuration: string;
    currentProgress: number; // Add current progress prop
}

// Generate a realistic-looking fallback waveform pattern
const generateFallbackAmplitudes = (samples: number = 20): number[] => {
    const amplitudes: number[] = [];
    // Create a natural-looking wave pattern with some randomness
    for (let i = 0; i < samples; i++) {
        // Base pattern: sine wave with varying frequency
        const base = Math.sin(i * 0.5) * 0.3 + 0.5;
        // Add controlled randomness for a more natural look
        const random = (Math.sin(i * 2.3) * 0.5 + 0.5) * 0.4;
        // Combine and ensure value is between 0.2 and 1
        const amplitude = Math.max(0.2, Math.min(1, base + random));
        amplitudes.push(amplitude);
    }
    return amplitudes;
};

const WaveformVisualizer: React.FC<WaveformVisualizerProps> = ({
    audioUrl,
    onSeek,
    currentProgress
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [amplitudes, setAmplitudes] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        let context: AudioContext | null = null;

        const loadAudio = async () => {
            try {
                setIsLoading(true);
                context = new AudioContext();
                const response = await fetch(audioUrl);
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch audio: ${response.status}`);
                }
                
                const arrayBuffer = await response.arrayBuffer();
                const audioBuffer = await context.decodeAudioData(arrayBuffer);

                // Process audio data for waveform bars only — do not play here.
                // Parent VoiceNote/Base64AudioPlayer owns the single <audio> element.
                const rawData = audioBuffer.getChannelData(0);
                const samples = 20; // number of bars
                const blockSize = Math.floor(rawData.length / samples);
                const newAmplitudes = [];

                for (let i = 0; i < samples; i++) {
                    let sum = 0;
                    for (let j = 0; j < blockSize; j++) {
                        sum += Math.abs(rawData[i * blockSize + j]);
                    }
                    newAmplitudes.push(sum / blockSize);
                }

                if (cancelled) {
                    return;
                }

                setAmplitudes(newAmplitudes);
                setIsLoading(false);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                // CORS or other fetch error - use fallback waveform
                console.warn('Could not load audio waveform (likely CORS), using fallback:', error);
                setAmplitudes(generateFallbackAmplitudes(20));
                setIsLoading(false);
            } finally {
                if (context && context.state !== 'closed') {
                    void context.close();
                }
            }
        };

        loadAudio();

        return () => {
            cancelled = true;
            if (context && context.state !== 'closed') {
                void context.close();
            }
        };
    }, [audioUrl]);

    // Update waveform when currentProgress changes
    useEffect(() => {
        const drawWaveform = () => {
            if (!canvasRef.current || amplitudes.length === 0) return;

            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const { width, height } = canvas;
            ctx.clearRect(0, 0, width, height);

            const barGap = 10; // Fixed gap between bars
            const totalGaps = (amplitudes.length - 1) * barGap;
            const barWidth = (width - totalGaps) / amplitudes.length; // Adjust barWidth accounting for gaps
            const maxAmplitude = Math.max(...amplitudes);
            const amplitudeScale = 0.9; // Keep the height scale

            // Draw background waveform
            amplitudes.forEach((amplitude, i) => {
                const normalizedHeight = (amplitude / maxAmplitude) * height * amplitudeScale;
                const x = i * (barWidth + barGap); // Position each bar with gap
                const y = (height - normalizedHeight) / 2;

                ctx.fillStyle = '#B3B3B3';
                ctx.fillRect(x, y, barWidth, normalizedHeight);
            });

            // Draw progress overlay
            const progressWidth = width * currentProgress;
            ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
            ctx.fillRect(0, 0, progressWidth, height);

            // Draw progress circle indicator
            const circleRadius = 8;
            const circleY = height / 2;
            const circleX = progressWidth;

            ctx.beginPath();
            ctx.arc(circleX, circleY, circleRadius, 0, 2 * Math.PI);
            ctx.fillStyle = '#000';
            ctx.fill();
            ctx.strokeStyle = '#B3B3B3';
            ctx.lineWidth = 2;
            ctx.stroke();
        };
        drawWaveform();
    }, [currentProgress, amplitudes]);



    const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const progress = x / canvas.width;

        onSeek(progress);
    };

    return (
        <div className="waveform-visualizer">
            {isLoading ? (
                <div className="loading">...</div>
            ) : (
                <canvas
                    ref={canvasRef}
                    onClick={handleClick}
                    className="waveform-canvas"
                />
            )}
        </div>
    );
};

export default WaveformVisualizer;